(() => {
  const CUSTOM_SORT = 'biggest-saving';
  const URL_PARAM = 'sort';
  const FETCH_CHUNK_SIZE = 4;
  const SHOW_SOLD_OUT_KEY = 'collection-show-sold-out';

  if (window.__collectionBiggestSaving) return;
  window.__collectionBiggestSaving = true;

  class BiggestSavingSort {
    constructor() {
      this.active = false;
      this.generation = 0;
      this.pageCache = new Map();

      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.customSort, this.onCustomSort.bind(this));
      document.addEventListener('collection:rerendered', this.onRerendered.bind(this));
      document.addEventListener('change', this.onSelectChange.bind(this), true);

      this.initFromUrl();
    }

    get grid() {
      return document.getElementById('ProductsList');
    }

    get gridContainer() {
      return document.getElementById('ProductGridContainer');
    }

    get sortSelects() {
      return document.querySelectorAll('select[is="facet-short"]');
    }

    get hideSoldOut() {
      if (document.querySelector('form[is="facet-form"][data-hide-sold-out]') === null) return false;
      return window.sessionStorage.getItem(SHOW_SOLD_OUT_KEY) !== '1';
    }

    get motionReduced() {
      return window.FoxTheme?.config?.motionReduced ?? true;
    }

    initFromUrl() {
      const params = new URLSearchParams(window.location.search);
      if (params.get(URL_PARAM) === CUSTOM_SORT) {
        this.active = true;
        this.syncSelects();
        this.apply();
      }
    }

    onCustomSort() {
      this.active = true;
      this.setUrlParam(true);
      this.syncSelects();
      this.scrollToCollection();
      this.apply();
    }

    onSelectChange(event) {
      const select = event.target;
      if (!select.matches || !select.matches('select[is="facet-short"]')) return;
      if (select.value !== CUSTOM_SORT) {
        this.active = false;
      }
    }

    onRerendered() {
      if (!this.active) return;
      this.setUrlParam(true);
      this.syncSelects();
      this.apply();
    }

    setUrlParam(on) {
      const url = new URL(window.location.href);
      if (on) {
        url.searchParams.set(URL_PARAM, CUSTOM_SORT);
        url.searchParams.delete('sort_by');
        url.searchParams.delete('page');
      } else {
        url.searchParams.delete(URL_PARAM);
      }
      url.searchParams.delete('section_id');
      history.replaceState({}, '', url.toString());
    }

    syncSelects() {
      this.sortSelects.forEach((select) => {
        if (select.querySelector(`option[value="${CUSTOM_SORT}"]`) && select.value !== CUSTOM_SORT) {
          select.value = CUSTOM_SORT;
          if (typeof select.calcSelectWidth === 'function') select.calcSelectWidth();
        }
      });
    }

    scrollToCollection() {
      const target = document.querySelector('.collection');
      if (!target) return;
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 200,
        behavior: this.motionReduced ? 'auto' : 'smooth',
      });
    }

    getSectionId() {
      const grid = this.grid;
      if (!grid) return null;
      const section = grid.closest('.shopify-section');
      return section ? section.id.replace('shopify-section-', '') : null;
    }

    buildPageUrl(page) {
      const url = new URL(window.location.href);
      ['page', URL_PARAM, 'sort_by', 'section_id'].forEach((key) => url.searchParams.delete(key));
      if (this.hideSoldOut && !url.searchParams.has('filter.v.availability')) {
        url.searchParams.set('filter.v.availability', '1');
      }
      const sectionId = this.getSectionId();
      if (sectionId) url.searchParams.set('section_id', sectionId);
      if (page > 1) url.searchParams.set('page', String(page));
      return url.toString();
    }

    savingOf(column) {
      const value = parseInt(column.dataset.saving, 10);
      return Number.isNaN(value) ? 0 : value;
    }

    parseGrid(html) {
      if (!html) return null;
      return new DOMParser().parseFromString(html, 'text/html').getElementById('ProductsList');
    }

    async fetchPage(page) {
      const url = this.buildPageUrl(page);
      if (this.pageCache.has(url)) return this.pageCache.get(url);
      const response = await fetch(url);
      if (!response.ok) return null;
      const html = await response.text();
      this.pageCache.set(url, html);
      return html;
    }

    async fetchPages(pages, gen) {
      const results = [];
      for (let i = 0; i < pages.length; i += FETCH_CHUNK_SIZE) {
        if (gen !== this.generation) break;
        const chunk = pages.slice(i, i + FETCH_CHUNK_SIZE);
        const chunkResults = await Promise.all(chunk.map((page) => this.fetchPage(page).catch(() => null)));
        results.push(...chunkResults);
      }
      return results;
    }

    async collectColumns(grid, gen) {
      let perPage = parseInt(grid.dataset.productsPerPage, 10) || 0;
      let count = parseInt(grid.dataset.productsCount, 10) || 0;

      const firstPage = this.parseGrid(await this.fetchPage(1).catch(() => null));
      let columns;
      if (firstPage) {
        columns = Array.from(firstPage.querySelectorAll('.product-column'));
        perPage = parseInt(firstPage.dataset.productsPerPage, 10) || perPage;
        count = parseInt(firstPage.dataset.productsCount, 10) || count;
      } else if (this.hideSoldOut) {
        return null;
      } else {
        columns = Array.from(grid.querySelectorAll('.product-column'));
      }

      const totalPages = perPage > 0 ? Math.ceil(count / perPage) : 1;
      const pages = [];
      for (let page = 2; page <= totalPages; page++) pages.push(page);

      const htmls = await this.fetchPages(pages, gen);
      htmls.forEach((html) => {
        const parsedGrid = this.parseGrid(html);
        if (parsedGrid) columns.push(...parsedGrid.querySelectorAll('.product-column'));
      });

      return columns;
    }

    dedupe(columns) {
      const seen = new Set();
      return columns.filter((column) => {
        const id = column.dataset.productId;
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    }

    startLoading(grid) {
      grid.setAttribute('aria-busy', 'true');
      document.querySelectorAll('[data-facet-loading]').forEach((el) => el.classList.add('btn--loading'));

      const container = this.gridContainer;
      if (container && window.FoxTheme?.Motion) {
        const translateY = this.motionReduced ? 0 : 50;
        FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);
      }
    }

    endLoading(grid) {
      grid.removeAttribute('aria-busy');
      document.querySelectorAll('[data-facet-loading]').forEach((el) => el.classList.remove('btn--loading'));

      const container = this.gridContainer;
      if (container && window.FoxTheme?.Motion) {
        const items = container.querySelectorAll('.product-card');
        const translateY = this.motionReduced ? 0 : 50;
        FoxTheme.Motion.timeline([
          [container, { y: [translateY, 0], opacity: [0, 1] }],
          [
            items,
            { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
            { duration: 0.5, delay: this.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
          ],
        ]);
      }
    }

    removePagination() {
      const container = this.gridContainer;
      if (!container) return;
      const pagination = container.querySelector('.pagination');
      if (pagination) pagination.remove();
    }

    reapplyLayout() {
      const layoutSwitcher = document.querySelector('layout-switcher');
      const active = layoutSwitcher && layoutSwitcher.querySelector('.btn--active');
      if (layoutSwitcher && active && typeof layoutSwitcher.onButtonClick === 'function') {
        layoutSwitcher.onButtonClick(active);
      }
    }

    async apply() {
      const grid = this.grid;
      if (!grid) return;

      const gen = ++this.generation;
      this.removePagination();
      this.startLoading(grid);

      let columns = null;
      try {
        columns = await this.collectColumns(grid, gen);
      } catch {
        columns = null;
      }

      if (gen !== this.generation || !this.active) return;

      if (columns) {
        const sorted = this.dedupe(columns).sort((a, b) => this.savingOf(b) - this.savingOf(a));
        const fragment = document.createDocumentFragment();
        sorted.forEach((column) => fragment.appendChild(column));
        grid.replaceChildren(fragment);
        this.reapplyLayout();
      }

      this.endLoading(this.grid || grid);
    }
  }

  const init = () => new BiggestSavingSort();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
